import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import SignalementStats from '../components/signalement/SignalementStats';
import SignalementFilters from '../components/signalement/SignalementFilters';
import SignalementList from '../components/signalement/SignalementList';
import SignalementDetail from '../components/signalement/SignalementDetail';
import useGlobale from '../components/global';
import useMenuItems from '../components/Menu';
import Sidebar from '../components/SideBar';
import api from '../components/api';
import { toast } from 'react-toastify';
import { RefreshCw, FileText, Users } from 'lucide-react';

const SignalementPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useGlobale();
  const { menuItems } = useMenuItems();
  
  // États pour les données
  const [signalements, setSignalements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedSignalement, setSelectedSignalement] = useState(null);
  const [activeTab, setActiveTab] = useState('publications'); // publications ou comptes
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    search: '',
    statut: '',
    type: '',
    ordering: '-created_at'
  });
  
  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth >= 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Charger les statistiques
  useEffect(() => {
    fetchStats();
  }, []);

  // Charger les signalements quand les filtres ou l'onglet changent
  useEffect(() => {
    fetchSignalements();
  }, [filters, activeTab]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/api/publication/admin/signalements/');
      
      // Calculer les stats
      const data = response.data;
      const statsData = {
        total: data.length,
        en_attente: data.filter(s => s.statut === 'EN_ATTENTE').length,
        resolu: data.filter(s => s.statut === 'RESOLU').length,
        rejete: data.filter(s => s.statut === 'REJETE').length
      };
      
      setStats(statsData);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchSignalements = async () => {
    try {
      setLoading(true);
      
      // Construire les paramètres de requête
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.statut) params.append('statut', filters.statut);
      if (filters.type) params.append('type', filters.type);
      if (filters.ordering) params.append('ordering', filters.ordering);
      
      const response = await api.get(`/api/publication/admin/signalements/?${params.toString()}`);
      setSignalements(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des signalements:', error);
      toast.error('Erreur lors du chargement des signalements');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      statut: '',
      type: '',
      ordering: '-created_at'
    });
  };

  const handleViewSignalement = (signalement) => {
    setSelectedSignalement(signalement);
  };

  const handleUpdateSignalement = (updatedSignalement) => {
    // Mettre à jour la liste
    setSignalements(prev =>
      prev.map(s => s.id === updatedSignalement.id ? updatedSignalement : s)
    );
    fetchStats();
  };

  const handleRefresh = () => {
    fetchSignalements();
    fetchStats();
    toast.info('Données actualisées');
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.statut) count++;
    if (filters.type) count++;
    return count;
  };

  return (
    <div className="flex min-h-screen bg-[#F4F8E9]">
      <Sidebar
        isOpen={isSidebarOpen}
        menuItems={menuItems}
        onClose={() => setIsSidebarOpen(false)}
      />

      <div className="flex-1 flex flex-col w-full">
        <Header
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          user={user}
        />

        <main className="flex-1 w-full p-4 lg:p-6 overflow-auto">
          {/* Header avec titre et bouton actualiser */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                Gestion des Signalements
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                Modérez et traitez les signalements de la plateforme
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-[#FF6B00] text-white rounded-lg hover:bg-[#e55d00] transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
          </div>

          {/* Statistiques */}
          {!statsLoading && stats && <SignalementStats stats={stats} />}

          {/* Onglets */}
          <div className="bg-white rounded-lg shadow-sm mb-6 flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('publications')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'publications'
                  ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText size={20} />
              Publications signalées
              {activeTab === 'publications' && signalements.length > 0 && (
                <span className="px-2 py-1 bg-[#FF6B00] text-white text-xs rounded-full">
                  {signalements.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('comptes')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'comptes'
                  ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Users size={20} />
              Comptes signalés
              <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                Bientôt
              </span>
            </button>
          </div>

          {/* Filtres */}
          <SignalementFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            activeFiltersCount={getActiveFiltersCount()}
          />

          {/* Résultats count */}
          {!loading && signalements.length > 0 && (
            <div className="mb-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-900">{signalements.length}</span> signalement{signalements.length > 1 ? 's' : ''} trouvé{signalements.length > 1 ? 's' : ''}
              </div>
            </div>
          )}

          {/* Contenu selon l'onglet */}
          {activeTab === 'publications' && (
            <SignalementList
              signalements={signalements}
              loading={loading}
              onView={handleViewSignalement}
              onUpdateStatus={handleUpdateSignalement}
            />
          )}

          {activeTab === 'comptes' && (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-[#FF6B00] to-[#437011] rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={40} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Signalements de comptes
              </h3>
              <p className="text-gray-600">
                Cette fonctionnalité sera disponible prochainement pour gérer les signalements de comptes utilisateurs.
              </p>
            </div>
          )}

          {/* Modal détail */}
          {selectedSignalement && (
            <SignalementDetail
              signalement={selectedSignalement}
              onClose={() => setSelectedSignalement(null)}
              onUpdate={handleUpdateSignalement}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default SignalementPage;